import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  CheckCircle2, 
  Circle, 
  RefreshCw, 
  Sparkles, 
  AlertCircle,
  Loader2,
  FileText,
  Users,
  Shield,
  Target,
  Calendar,
  TrendingUp
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getDefaultProvider } from '@/ai/providers';
import { getRecommendedModel } from '@/ai/models';
import { generateAIText } from "@/lib/ai-text";

interface ComplianceChecklistItem {
  id: string;
  title: string;
  description?: string;
  order_index: number;
}

interface ComplianceChecklist {
  id: string;
  policy_id: string;
  company_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  items: ComplianceChecklistItem[];
}

interface ComplianceChecklistProps {
  policyId: string;
  policyTitle: string;
  policyContent: string;
  policyCreatedBy: string;
  isPolicyCreator: boolean;
}

const ComplianceChecklist = ({ 
  policyId, 
  policyTitle, 
  policyContent, 
  policyCreatedBy, 
  isPolicyCreator 
}: ComplianceChecklistProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [checklist, setChecklist] = useState<ComplianceChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Load existing checklist
  const loadChecklist = async () => {
    if (!profile?.company_id) return;

    try {
      const { data: checklistData, error: checklistError } = await supabase
        .from('compliance_checklists')
        .select(`
          id,
          policy_id,
          company_id,
          created_by,
          created_at,
          updated_at,
          items:compliance_checklist_items (
            id,
            title,
            description,
            order_index
          )
        `)
        .eq('policy_id', policyId)
        .eq('company_id', profile.company_id)
        .single();

      if (checklistError && checklistError.code !== 'PGRST116') {
        throw checklistError;
      }

      if (checklistData) {
        // Sort items by order_index
        const sortedItems = checklistData.items.sort((a, b) => a.order_index - b.order_index);
        setChecklist({ ...checklistData, items: sortedItems });
      }
    } catch (error: any) {
      console.error('Error loading compliance checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate AI checklist
  const generateChecklist = async () => {
    if (!profile?.company_id) return;

    setGenerating(true);
    try {
      // Check if API key is available
      const apiKey = localStorage.getItem("openai_api_key");
      if (!apiKey) {
        throw new Error("OpenAI API key is required. Please go to Settings and add your OpenAI API key to generate compliance checklists.");
      }

      // Create AI prompt for compliance checklist generation
      const prompt = `You are a compliance expert. Based on the following policy, generate exactly 10 actionable compliance checklist items that organizations should implement to ensure they follow this policy effectively.

Policy Title: ${policyTitle}

Policy Content:
${policyContent}

For each checklist item, provide:
1. A clear, actionable title (max 80 characters)
2. A brief description explaining what needs to be done (max 200 characters)

Format your response as a JSON array with this exact structure:
[
  {
    "title": "Item title here",
    "description": "Brief description of what needs to be done"
  }
]

Focus on practical implementation steps that organizations commonly miss. Make items specific and measurable.`;

      // Use client-side AI to generate checklist
      const provider = getDefaultProvider();
      const model = getRecommendedModel(provider, 'ANALYSIS');
      
      const response = await generateAIText({
        provider,
        model,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
      });
      
      // Parse the JSON response
      let checklistItems;
      try {
        // Extract JSON from the response (in case there's extra text)
        const jsonMatch = response.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          checklistItems = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No valid JSON found in response');
        }
      } catch (parseError) {
        console.error('Failed to parse AI response:', response);
        throw new Error('Failed to parse AI response. Please try again.');
      }

      // Validate that we have exactly 10 items
      if (!Array.isArray(checklistItems) || checklistItems.length !== 10) {
        throw new Error('AI response must contain exactly 10 checklist items');
      }

      // Validate each item has required fields
      for (const item of checklistItems) {
        if (!item.title || !item.description) {
          throw new Error('Each checklist item must have a title and description');
        }
      }

      // Create or update checklist in database
      const { data: checklistData, error: checklistError } = await supabase
        .from('compliance_checklists')
        .upsert({
          policy_id: policyId,
          company_id: profile.company_id,
          created_by: profile.id,
        })
        .select()
        .single();

      if (checklistError) throw checklistError;

      // Delete existing items
      await supabase
        .from('compliance_checklist_items')
        .delete()
        .eq('checklist_id', checklistData.id);

      // Insert new items
      const itemsToInsert = checklistItems.map((item: any, index: number) => ({
        checklist_id: checklistData.id,
        title: item.title,
        description: item.description,
        order_index: index,
      }));

      const { error: itemsError } = await supabase
        .from('compliance_checklist_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Reload checklist
      await loadChecklist();

      toast({
        title: "Success",
        description: "Compliance checklist generated successfully!",
      });

    } catch (error: any) {
      console.error('Error generating checklist:', error);
      toast({
        title: "Error",
        description: "Failed to generate compliance checklist: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
      setIsDialogOpen(false);
    }
  };

  useEffect(() => {
    loadChecklist();
  }, [policyId, profile?.company_id]);

  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-muted-foreground">Loading compliance checklist...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <CardTitle className="text-lg truncate">Compliance Checklist</CardTitle>
            <Badge variant="secondary" className="ml-2 hidden sm:inline-flex">
              AI Generated
            </Badge>
          </div>
          {isPolicyCreator && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={generating} className="flex-shrink-0">
                  <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${generating ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{generating ? 'Generating...' : 'Regenerate'}</span>
                  <span className="sm:hidden">{generating ? '...' : 'Regen'}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Regenerate Compliance Checklist</DialogTitle>
                  <DialogDescription>
                    This will generate a new AI-powered compliance checklist based on the current policy content. 
                    The existing checklist will be replaced.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={generateChecklist} disabled={generating}>
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Checklist
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!checklist ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No Compliance Checklist Yet
                </h3>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Generate an AI-powered compliance checklist to help your organization implement this policy effectively.
                </p>
                {isPolicyCreator && (
                  <Button onClick={generateChecklist} disabled={generating}>
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Checklist
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Generated on {new Date(checklist.created_at).toLocaleDateString()}</span>
              {checklist.updated_at !== checklist.created_at && (
                <>
                  <span>•</span>
                  <span>Updated on {new Date(checklist.updated_at).toLocaleDateString()}</span>
                </>
              )}
            </div>
            
            <div className="space-y-3">
              {checklist.items.map((item, index) => (
                <div key={item.id} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2 mt-1">
                    <Circle className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground mb-1">
                      {item.title}
                    </h4>
                    {item.description && (
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">
                    Implementation Tips
                  </h4>
                  <p className="text-sm text-blue-800">
                    Use this checklist as a roadmap for policy implementation. Each item represents a critical step 
                    that organizations often overlook. Consider assigning responsibilities and setting deadlines for each item.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplianceChecklist;
